// cspell:ignore firestore Firestore Mocktails Winemaking
// DrinkDetailPage.js
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RestaurantContext } from "./contexts/RestaurantContext";
import { menuService } from "./services/menuService";
import WineryMap from "./WineryMap";
import WineLabel from "./WineLabel";
import ProfessionalRatings from "./components/ProfessionalRatings";
import ProducerInformation from "./components/ProducerInformation";

const DrinkDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentRestaurant } = useContext(RestaurantContext);
  const [drink, setDrink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDrink = async () => {
      if (!currentRestaurant?.id || !id) {
        setError("Restaurant or drink not found");
        setLoading(false);
        return;
      }

      try {
        const drinkData = await menuService.getMenuItem(
          currentRestaurant.id,
          id
        );
        if (drinkData) {
          // Check if this is a category that shouldn't have a detail page
          const simpleCategories = [
            "Specialty Cocktails",
            "Sangria by the Glass",
            "Mocktails",
            "Beers",
          ];
          if (simpleCategories.includes(drinkData.category)) {
            // Redirect back to restaurant menu if this is a simple category
            const menuUrl = currentRestaurant.slug
              ? `/restaurant/${currentRestaurant.slug}/menu`
              : "/drinks";
            navigate(menuUrl);
            return;
          }

          setDrink(drinkData);
        } else {
          setError("Drink not found");
        }
      } catch (err) {
        setError("Error loading drink details");
        console.error("Error fetching drink:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDrink();
  }, [id, navigate, currentRestaurant?.id, currentRestaurant?.slug]);

  const handleBackNavigation = () => {
    const savedCategory = localStorage.getItem("selectedCategory");
    const baseUrl = currentRestaurant?.slug
      ? `/restaurant/${currentRestaurant.slug}/menu`
      : "/drinks";

    if (savedCategory && savedCategory !== "All") {
      navigate(`${baseUrl}?category=${encodeURIComponent(savedCategory)}`);
    } else {
      navigate(baseUrl);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "Price on request";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  // Convert legacy review format to AI-enhanced ratings format
  const convertLegacyRatingsToAI = (reviews) => {
    if (!reviews || typeof reviews !== "object") return [];

    const ratings = [];

    // Handle new format
    if (reviews["Wine Spectator"]) {
      ratings.push({
        source: "Wine Spectator",
        score: reviews["Wine Spectator"].rating,
        scale: 100,
        review: reviews["Wine Spectator"].review,
        date: reviews["Wine Spectator"].date || new Date().toISOString(),
      });
    }

    if (reviews["Wine Enthusiast"]) {
      ratings.push({
        source: "Wine Enthusiast",
        score: reviews["Wine Enthusiast"].rating,
        scale: 100,
        review: reviews["Wine Enthusiast"].review,
        date: reviews["Wine Enthusiast"].date || new Date().toISOString(),
      });
    }

    if (reviews["James Suckling"]) {
      ratings.push({
        source: "James Suckling",
        score: reviews["James Suckling"].rating,
        scale: 100,
        review: reviews["James Suckling"].review,
        date: reviews["James Suckling"].date || new Date().toISOString(),
      });
    }

    // Handle legacy format
    if (!reviews["Wine Spectator"] && reviews.wineSpectator) {
      ratings.push({
        source: "Wine Spectator",
        score: reviews.wineSpectator.rating,
        scale: 100,
        review: reviews.wineSpectator.note,
        date: new Date().toISOString(),
      });
    }

    if (!reviews["Wine Enthusiast"] && reviews.wineEnthusiast) {
      ratings.push({
        source: "Wine Enthusiast",
        score: reviews.wineEnthusiast.rating,
        scale: 100,
        review: reviews.wineEnthusiast.note,
        date: new Date().toISOString(),
      });
    }

    return ratings;
  };

  // Convert legacy producer/winery format to AI-enhanced producer format
  const convertLegacyProducerToAI = (wineryInfo, location, drink) => {
    if (!wineryInfo && !drink.producer && !drink.winery) return null;

    return {
      name: drink.producer || drink.winery || "Unknown Producer",
      founded: wineryInfo?.established || null,
      location: {
        country: location?.country || "Unknown",
        region: location?.region || "Unknown Region",
        subregion: location?.subregion || null,
        coordinates: location?.coordinates || null,
      },
      history: wineryInfo?.history || null,
      philosophy: wineryInfo?.philosophy || null,
      signature: wineryInfo?.signature || null,
      accolades: wineryInfo?.accolades || null,
      techniques: wineryInfo?.techniques || [],
      size: wineryInfo?.size || "Unknown",
      certifications: wineryInfo?.certifications || [],
      socialMedia: wineryInfo?.socialMedia || {},
      website: wineryInfo?.website || null,
    };
  };

  // Determine if this is a wine
  // Note: This variable appears to be unused, so we're removing it to fix the SonarLint warning

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-amber-800">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-800"></div>
          <span className="text-lg">Loading drink details...</span>
        </div>
      </div>
    );
  }

  if (error || !drink) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 text-amber-400 mx-auto mb-4">🍷</div>
          <h2 className="text-2xl font-bold text-amber-900 mb-2">
            Drink Not Found
          </h2>
          <p className="text-amber-700 mb-4">
            {error || "The requested drink could not be found."}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const aiEnhancements = drink.aiEnhancements || {};
  const images = aiEnhancements.images || {};
  const tastingNotes = drink.tastingNotes || aiEnhancements.tastingNotes || {};
  const reviews = drink.professionalReviews || aiEnhancements.reviews || {};
  const varietals = drink.varietals || [];
  const wineDetails = drink.wineDetails || {};
  const wineClassification = aiEnhancements.wineClassification || {};
  const location = wineClassification.location || {};
  const vintageInfo = wineClassification.vintageInfo || {};
  const wineryInfo = wineClassification.wineryInfo || {};

  // Determine wine type from AI classification or fallback to category
  const wineType =
    wineClassification.wineType ||
    (drink.category?.toLowerCase().includes("wine") ? "wine" : drink.category);

  return (
    <div className="detail-page-bg">
      {/* Skip links for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <a href="#wine-details" className="skip-link">
        Skip to wine details
      </a>

      {/* Header */}
      <header
        className="detail-hero-bg"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ maxWidth: "96rem", margin: "0 auto", padding: "1rem" }}>
          <button
            onClick={handleBackNavigation}
            className="back-button"
            style={{ marginBottom: "1rem" }}
            aria-label="Go back to drinks menu"
          >
            ← Back to Menu
          </button>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div>
              <h1
                className="detail-wine-header"
                style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
              >
                {drink.name} {drink.vintage}
              </h1>
              <div
                className="detail-wine-info"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                {(drink.producer || drink.winery) && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                    aria-label="Producer"
                  >
                    {drink.producer || drink.winery}
                  </span>
                )}
                {drink.region && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                    aria-label="Region"
                  >
                    {drink.region}
                  </span>
                )}
                {drink.vintage && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                    aria-label="Vintage year"
                  >
                    📅 {drink.vintage}
                  </span>
                )}
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div
                className="detail-price-display"
                style={{ fontSize: "2rem" }}
                aria-label="Price"
              >
                {formatPrice(drink.price)}
              </div>
              {drink.bin && (
                <div
                  style={{ color: "var(--accent-gold)" }}
                  aria-label="Bin number"
                >
                  Bin #{drink.bin}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-6xl mx-auto px-4 py-8">
        {/* Wine layout */}
        <section
          id="wine-details"
          className="grid lg:grid-cols-3 gap-8"
          aria-label="Wine details and information"
        >
          {/* Left Column - Image and Basic Info */}
          <div className="lg:col-span-1">
            {/* Wine Label */}
            <WineLabel drink={drink} images={images} className="mb-6" />

            {/* Quick Info Card */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="font-bold text-lg text-amber-900 mb-4">
                Wine Details
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium capitalize">{wineType}</span>
                </div>

                {/* Location Information - Country first, then Region, then Appellation */}
                <div className="flex justify-between">
                  <span className="text-gray-600">Country:</span>
                  <span className="font-medium">
                    {drink.country || location.country || "N/A"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Region:</span>
                  <span className="font-medium">
                    {drink.region || location.region || "N/A"}
                  </span>
                </div>

                {(drink.appellation || location.appellation) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Appellation:</span>
                    <span className="font-medium">
                      {drink.appellation || location.appellation}
                    </span>
                  </div>
                )}

                {(drink.subRegion || location.subRegion) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sub-Region:</span>
                    <span className="font-medium">
                      {drink.subRegion || location.subRegion}
                    </span>
                  </div>
                )}

                {/* Enhanced wine details */}
                {(vintageInfo.vintage ||
                  wineDetails.vintage ||
                  drink.vintage) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vintage:</span>
                    <span className="font-medium">
                      {vintageInfo.vintage ||
                        wineDetails.vintage ||
                        drink.vintage}
                    </span>
                  </div>
                )}

                {(wineDetails.alcoholContent || drink.abv) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ABV:</span>
                    <span className="font-medium">
                      {wineDetails.alcoholContent || `${drink.abv}%`}
                    </span>
                  </div>
                )}

                {wineDetails.servingTemperature && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Serving Temp:</span>
                    <span className="font-medium text-sm">
                      {wineDetails.servingTemperature}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - All Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6 wine-detail-container">
              {/* Varietals Breakdown */}
              {varietals.length > 0 && (
                <div className="wine-detail-section">
                  <h3 className="font-bold text-xl text-amber-900 mb-4 flex items-center gap-2">
                    🍇 Grape Varietals
                  </h3>
                  <div className="bg-gradient-to-r from-purple-50 to-red-50 p-6 rounded-lg">
                    <div className="space-y-4">
                      {varietals.map((varietal) => (
                        <div
                          key={`varietal-${varietal.grape}`}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-purple-600 font-bold text-lg min-w-[60px]">
                              {varietal.percentage}%
                            </span>
                            <span className="font-medium text-purple-800 text-lg">
                              {varietal.grape}
                            </span>
                          </div>
                          <div className="w-32 bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${varietal.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* About This Vintage */}
              {(vintageInfo.growingConditions ||
                vintageInfo.winemaker ||
                vintageInfo.harvestNotes ||
                drink.description) && (
                <div className="wine-detail-section">
                  <h3 className="font-bold text-xl text-amber-900 mb-4">
                    About This Vintage
                  </h3>
                  <div className="space-y-4">
                    {vintageInfo.growingConditions && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-2">
                          Growing Conditions
                        </h4>
                        <p className="text-green-700 leading-relaxed">
                          {vintageInfo.growingConditions}
                        </p>
                      </div>
                    )}

                    {vintageInfo.harvestNotes && (
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <h4 className="font-semibold text-orange-800 mb-2">
                          Harvest Notes
                        </h4>
                        <p className="text-orange-700 leading-relaxed">
                          {vintageInfo.harvestNotes}
                        </p>
                      </div>
                    )}

                    {vintageInfo.winemaker && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          Winemaker Notes
                        </h4>
                        <p className="text-blue-700 leading-relaxed">
                          {vintageInfo.winemaker}
                        </p>
                      </div>
                    )}

                    {drink.description &&
                      !vintageInfo.growingConditions &&
                      !vintageInfo.winemaker &&
                      !vintageInfo.harvestNotes && (
                        <p className="text-gray-700 leading-relaxed text-lg">
                          {drink.description}
                        </p>
                      )}
                  </div>
                </div>
              )}

              {/* Producer Information - Enhanced with AI */}
              <ProducerInformation
                beverage={{
                  ...drink,
                  aiEnhanced: {
                    ...drink.aiEnhanced,
                    producer: convertLegacyProducerToAI(
                      wineryInfo,
                      location,
                      drink
                    ),
                  },
                }}
                showMap={true}
                compact={false}
              />

              {/* Enhanced Tasting Notes */}
              {(tastingNotes.appearance ||
                tastingNotes.nose ||
                tastingNotes.palate ||
                tastingNotes.finish) && (
                <div className="wine-detail-section">
                  <h3 className="font-bold text-xl text-amber-900 mb-4 flex items-center gap-2">
                    Detailed Tasting Notes
                  </h3>
                  <div className="space-y-6">
                    {tastingNotes.appearance && (
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 wine-detail-card">
                        <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                          👁️ Appearance
                        </h4>
                        <p className="text-yellow-700 text-lg wine-detail-paragraph">
                          {tastingNotes.appearance}
                        </p>
                      </div>
                    )}

                    {tastingNotes.nose && (
                      <div className="bg-rose-50 p-4 rounded-lg border border-rose-200 wine-detail-card">
                        <h4 className="font-semibold text-rose-800 mb-2 flex items-center gap-2">
                          👃 Nose
                        </h4>
                        <p className="text-rose-700 text-lg wine-detail-paragraph">
                          {tastingNotes.nose}
                        </p>
                      </div>
                    )}

                    {tastingNotes.palate && (
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 wine-detail-card">
                        <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                          👅 Palate
                        </h4>
                        <p className="text-purple-700 text-lg">
                          {tastingNotes.palate}
                        </p>
                      </div>
                    )}

                    {tastingNotes.finish && (
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 wine-detail-card">
                        <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                          ✨ Finish
                        </h4>
                        <p className="text-amber-700 text-lg">
                          {tastingNotes.finish}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Fallback to old format tasting notes */}
              {!tastingNotes.appearance &&
                !tastingNotes.nose &&
                !tastingNotes.palate &&
                !tastingNotes.finish &&
                tastingNotes.aroma && (
                  <div>
                    <h3 className="font-bold text-xl text-amber-900 mb-4 flex items-center gap-2">
                      👁️ Tasting Notes
                    </h3>
                    <div className="space-y-6">
                      <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                        <h4 className="font-semibold text-rose-800 mb-2">
                          Aroma
                        </h4>
                        <p className="text-rose-700 text-lg">
                          {tastingNotes.aroma}
                        </p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h4 className="font-semibold text-purple-800 mb-2">
                          Flavor
                        </h4>
                        <p className="text-purple-700 text-lg">
                          {tastingNotes.flavor}
                        </p>
                      </div>
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <h4 className="font-semibold text-amber-800 mb-2">
                          Finish
                        </h4>
                        <p className="text-amber-700 text-lg">
                          {tastingNotes.finish}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Food Pairings */}
              {wineDetails.foodPairings &&
                wineDetails.foodPairings.length > 0 && (
                  <div className="wine-detail-section">
                    <h3 className="font-bold text-xl text-amber-900 mb-4 flex items-center gap-2">
                      🍽️ Recommended Food Pairings
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {wineDetails.foodPairings.map((pairing) => (
                        <div
                          key={`food-pairing-${pairing}`}
                          className="bg-green-50 p-4 rounded-lg border border-green-200"
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-green-600 mt-1 flex-shrink-0 text-xl">
                              🍴
                            </span>
                            <span className="text-green-800 font-medium text-lg">
                              {pairing}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Cellaring Information */}
              {wineDetails.cellaring && (
                <div className="wine-detail-section">
                  <h3 className="font-bold text-xl text-amber-900 mb-4 flex items-center gap-2">
                    🏛️ Cellaring & Aging
                  </h3>
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-amber-800 text-lg">
                      {wineDetails.cellaring}
                    </p>
                  </div>
                </div>
              )}

              {/* Professional Reviews & Ratings - Enhanced with AI */}
              <ProfessionalRatings
                beverage={{
                  ...drink,
                  aiEnhanced: {
                    ...drink.aiEnhanced,
                    ratings: convertLegacyRatingsToAI(reviews),
                  },
                }}
                showDetails={true}
                compact={false}
              />

              {/* Winery Map */}
              <div className="wine-detail-section">
                <h3 className="font-bold text-xl text-amber-900 mb-4 flex items-center gap-2">
                  🗺️ Winery Location
                </h3>
                <WineryMap drink={drink} />
              </div>
            </div>
          </div>
        </section>

        {/* AI Enhancement Notice */}
        {drink.aiEnhancements && (
          <div className="mt-8 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-purple-700">
              <span aria-hidden="true">✨</span>
              <span className="text-sm">
                Enhanced with AI-powered wine expertise • Last updated:{" "}
                {new Date(
                  drink.lastEnhanced || drink.createdAt
                ).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DrinkDetailPage;
